// Actual domain logic should be in a domainLogic.js file, at the appropriate
// level in the hierarchy.
import { IN_TRANSACTION, ITEM_CALCULATED_FIELDS } from "appConfig";

import { Decimal, Money } from "classes/DecimalClasses";

import {
  updateLineItemSerials,
  updateLineRelatedRecordsItem,
} from "services/sosInventoryService/domainLogic";
import { getEmptyRecord } from "services/sosInventoryService/itemReceipt/schema";
import {
  getRecord,
  calculateDueDate,
  getExchangeRate,
  getRecordFrom,
} from "services/sosInventoryService/sosApi";
import { setBinForInboundLines } from "services/utility/bins";
import {
  copyCustomFieldValues,
  reconcileCustomFields,
  getCustomFieldDefinitions,
} from "services/utility/customFields";
import { yyyymmddToDate } from "services/utility/dates";
import { setPageDirty } from "services/utility/edit";
import { getRelatedReferenceObjects } from "services/utility/edit";
import { handleProgramError } from "services/utility/errors";
import { isNonZeroDecimalOrMoney } from "services/utility/misc";

import globalState from "globalState/globalState";
import { linesLoadingOn, linesLoadingOff } from "globalState/loadingSlice";

import { OBJECT_TYPES, DEFAULT_DECIMALS_ROUNDED } from "appConstants";
import { EMPTY_OTHER_COST, EMPTY_LINE_ITEM } from "editConfig";

const OBJECT_TYPE = OBJECT_TYPES.ITEM_RECEIPT.fullString;

export async function getTransaction(
  id,
  newFromId,
  newFromObjectType,
  updaters,
  customFieldDefs,
  recordToReload
) {
  let record, lines, otherCosts, relatedRecords;

  if (id || newFromObjectType || recordToReload) {
    if (recordToReload) {
      record = recordToReload;
    } else if (id) {
      record = await getRecord(OBJECT_TYPE, id);
    } else {
      // it's a "populate from" request
      record = await getRecordFrom(OBJECT_TYPE, newFromObjectType, newFromId);
      const autoSerialLotNumbers =
        globalState.getState().userCompanySettings.settings
          .autoSerialLotNumbers;
      record.autoSerialLotNumbering = autoSerialLotNumbers;
      delete record.id;
    }

    record.customFields = reconcileCustomFields(
      customFieldDefs,
      record.customFields
    );

    lines = record.lines;
    otherCosts = record.otherCosts;
    delete record.lines;
    delete record.otherCosts;

    // render lines immediately while async fields are computed in the background
    globalState.dispatch(linesLoadingOn());
    updaters.lineHandler({ type: "set", lines });
    updaters.otherCostHandler({ type: "set", lines: otherCosts });
    if (otherCosts && otherCosts.length === 0) {
      setPageDirty();
      updaters.otherCostHandler({
        type: "insert",
        insertAt: 1,
        newLine: EMPTY_OTHER_COST[OBJECT_TYPE],
      });
    }
    updaters.setRecord(record);

    relatedRecords = await getRelatedReferenceObjects(OBJECT_TYPE, record);
    updaters.setRelatedRecords(relatedRecords);

    // get items for each line item, so that we have updated item info for
    // the calculations below
    lines = await updateLineRelatedRecordsItem(
      record.location,
      record.date,
      lines,
      ITEM_CALCULATED_FIELDS[OBJECT_TYPE]
    );

    // patch only async-computed fields into the already-rendered lines
    updaters.lineHandler({
      type: "updateFields",
      lines,
      fields: ["relatedRecords"],
    });
    globalState.dispatch(linesLoadingOff());
  } else {
    // new record
    const { settings } = globalState.getState().userCompanySettings;
    const defaultLocation =
      globalState.getState().userCompanySettings.settings.defaultLocation;
    const numLinesToAdd =
      globalState.getState().userCompanySettings.settings.numLinesToAdd;
    const defaultLocationForPurchasing =
      globalState.getState().userCompanySettings.settings
        .defaultLocationForPurchasing;

    record = {
      ...getEmptyRecord(settings),
      customFields: reconcileCustomFields(customFieldDefs, []),
    };

    lines = Array(numLinesToAdd).fill(EMPTY_LINE_ITEM[OBJECT_TYPE]);
    updaters.otherCostHandler({
      type: "set",
      lines: [EMPTY_OTHER_COST[OBJECT_TYPE]],
    });

    relatedRecords = {};

    if (defaultLocation) {
      const txnLocation =
        defaultLocationForPurchasing || defaultLocation || null;
      record.location = txnLocation;

      const location = await getRecord(
        "location",
        txnLocation.id,
        IN_TRANSACTION
      );
      record.shipping = location;
      relatedRecords = { ...relatedRecords, location };
    }
    updaters.setRelatedRecords(relatedRecords);
    updaters.setRecord(record);
    updaters.lineHandler({ type: "set", lines });
  }
}

export async function updateTransaction(
  field,
  newValue,
  transactionCustomFieldDefs,
  currentState,
  updaters,
  currencies
) {
  const { record, lines, relatedRecords, totals } = currentState;
  if (!record || !lines || !relatedRecords) {
    handleProgramError(new Error("update Transaction | invalid currentState"));
  }

  let newRecord, newLines, newRelatedRecords;

  switch (field) {
    case "date":
      newRecord = { ...record, date: newValue };
      updaters.setRecord(newRecord);

      if (!record.calculateDueDateFromInvoiceDate && record.terms && newValue) {
        const response = await calculateDueDate(record.terms.id, newValue);
        const dueDate = yyyymmddToDate(response.dueDate);
        newRecord = {
          ...record,
          date: newValue,
          vendorInvoiceDueDate: dueDate,
        };
        updaters.setRecord(newRecord);
      }

      break;

    case "location":
      globalState.dispatch(linesLoadingOn());
      try {
        newRecord = { ...record, location: newValue };
        updaters.setRecord(newRecord);
        const location = newValue
          ? await getRecord("location", newValue?.id)
          : null;
        newRelatedRecords = { ...relatedRecords, location };

        const updatedLines = await setBinForInboundLines(lines, location);
        updaters.lineHandler({
          type: "updateFields",
          lines: updatedLines,
          fields: ["bin"],
        });
      } catch (e) {
        handleProgramError(e);
      } finally {
        globalState.dispatch(linesLoadingOff());
      }

      break;
    case "calculateDueDateFromInvoiceDate":
      newRecord = { ...record, [field]: newValue };

      // if the vendor has default terms, recalculate due date with new terms
      newRecord.vendorInvoiceDueDate = null;
      if (record.terms && (!newValue || record.vendorInvoiceDate)) {
        const { dueDate } = await calculateDueDate(
          record.terms.id,
          newValue ? record.vendorInvoiceDate : record.date
        );
        newRecord.vendorInvoiceDueDate = yyyymmddToDate(dueDate);
      }

      updaters.setRecord(newRecord);
      break;
    case "terms":
      newRecord = {
        ...record,
        terms: newValue?.id ? { id: newValue.id, name: newValue.name } : null,
      };

      // calculate due date
      if (
        !newValue?.id ||
        (record.calculateDueDateFromInvoiceDate && !record.vendorInvoiceDate)
      ) {
        updaters.setRecord(newRecord);
        return;
      }

      const { dueDate } = await calculateDueDate(
        newValue.id,
        record.calculateDueDateFromInvoiceDate
          ? record.vendorInvoiceDate
          : record.date
      );
      newRecord.vendorInvoiceDueDate = yyyymmddToDate(dueDate);
      updaters.setRecord(newRecord);
      break;

    case "vendorInvoiceDate":
      // calculate due date
      if (newValue && record.terms) {
        const response = await calculateDueDate(record.terms.id, newValue);
        const dueDate = yyyymmddToDate(response.dueDate);
        newRecord = {
          ...record,
          vendorInvoiceDate: newValue,
          vendorInvoiceDueDate: dueDate,
        };
      } else {
        newRecord = { ...record, vendorInvoiceDate: newValue };
      }
      updaters.setRecord(newRecord);

      break;

    case "comment":
    case "customFields":
    case "department":
    case "number":
    case "payment":
    case "updateDefaultCosts":
    case "vendorInvoiceDueDate":
    case "vendorMessage":
    case "vendorNotes":
      newRecord = { ...record, [field]: newValue };
      updaters.setRecord(newRecord);

      break;

    case "exchangeRate":
      newRecord = { ...record, [field]: new Decimal(newValue || 0) };
      updaters.setRecord(newRecord);
      break;

    case "depositPercent":
      const depositPercent = new Decimal(newValue || 0);
      newRecord = {
        ...record,
        depositPercent,
        depositAmount: totals.total
          .times(depositPercent)
          .times(new Decimal(0.01)),
      };
      updaters.setRecord(newRecord);
      break;

    case "depositAmount":
      const depositAmount = new Money(newValue || 0);
      const percent = isNonZeroDecimalOrMoney(totals.total)
        ? depositAmount
            .div(totals.total)
            .times(new Decimal(100))
            .round(DEFAULT_DECIMALS_ROUNDED, Decimal.roundDown)
        : null;
      newRecord = { ...record, depositAmount, depositPercent: percent };
      updaters.setRecord(newRecord);
      break;

    case "vendor":
      // if the field is empty, null the field
      if (!newValue) {
        newRecord = { ...record, vendor: null };
        updaters.setRecord(newRecord);
        newRelatedRecords = { ...relatedRecords, vendor: null };
        break;
      }

      const vendorCustomFieldDefs = await getCustomFieldDefinitions("vendor");

      try {
        // get the newly selected vendor record, so that we can...
        const vendor = await getRecord("vendor", newValue.id, IN_TRANSACTION);

        // see if the vendor has a currency setting; if so, get the exchange rate
        const currencyResponse = vendor?.currency
          ? await getExchangeRate(vendor.currency.name, record.date)
          : null;

        // if the vendor has default terms, recalculate due date with new terms
        let vendorInvoiceDueDate = null;
        if (
          vendor.terms &&
          (!record.calculateDueDateFromInvoiceDate || record.vendorInvoiceDate)
        ) {
          const { dueDate } = await calculateDueDate(
            vendor.terms.id,
            record.calculateDueDateFromInvoiceDate
              ? record.vendorInvoiceDate
              : record.date
          );
          vendorInvoiceDueDate = yyyymmddToDate(dueDate);
        }

        // be sure there are custom field entries for each defined custom field
        // in the vendor record...
        const vendorCustomFields = reconcileCustomFields(
          vendorCustomFieldDefs,
          vendor.customFields
        );

        const newTransactionCustomFields = copyCustomFieldValues(
          vendorCustomFieldDefs,
          vendorCustomFields,
          transactionCustomFieldDefs,
          record.customFields
        );

        // ...then initialize any transaction custom fields to their matching vendor
        // custom field values, if any

        newRecord = {
          ...record,
          vendor: newValue,
          currency: currencyResponse ? vendor.currency : record.currency,
          exchangeRate: currencyResponse
            ? currencyResponse.exchangeRate
            : record.exchangeRate,
          vendorNotes: vendor.notes,
          taxCode: vendor.taxCode,
          terms: vendor.terms,
          vendorInvoiceDueDate,
          customFields: newTransactionCustomFields,
        };
        updaters.setRecord(newRecord);
        newRelatedRecords = { ...relatedRecords, vendor };
      } catch (e) {
        handleProgramError(e);
      }

      break;

    case "currency":
      const { id: newId, name: newName } = newValue || {};
      updaters.setRecord((prev) => ({
        ...prev,
        currency: newId ? { id: newId, name: newName } : null,
        exchangeRate: newId
          ? currencies.find(({ id }) => id === newId).exchangeRate
          : null,
      }));
      break;

    case "autoSerialLotNumbering":
      newRecord = { ...record, [field]: newValue };
      updaters.setRecord(newRecord);
      newLines = updateLineItemSerials(newValue, lines);
      updaters.lineHandler({ type: "set", lines: newLines });
      break;

    default:
      handleProgramError(
        new Error(`updateTransaction | unknown field (${field})`)
      );
  }

  if (newRelatedRecords) {
    updaters.setRelatedRecords(newRelatedRecords);
  }
}
